const SECTION_HEADER = 'cart-items-component';

function drawerSectionId(node) {
  return node.closest(SECTION_HEADER)?.dataset.sectionId || '';
}

async function readCart(response) {
  const data = await response.json();
  if (!response.ok) {
    const message = data.description || data.message || 'Could not update the cart';
    throw new Error(message);
  }
  return data;
}

function sectionMarkup(cart, sectionId) {
  const sections = cart?.sections;
  if (!sections) return '';
  if (typeof sections[sectionId] === 'string') return sections[sectionId];
  return Object.values(sections).find((html) => typeof html === 'string' && html.includes('cart-drawer__inner')) || '';
}

function itemCountFrom(html, cart) {
  if (typeof cart?.item_count === 'number') return cart.item_count;
  if (!html) return null;
  const text = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
  const count = text ? Number.parseInt(text, 10) : NaN;
  return Number.isNaN(count) ? null : count;
}

function drawerBodyPresent(root) {
  return Boolean(root?.querySelector('.cart-drawer__content, .cart-drawer__heading--empty'));
}

function replaceDrawerInner(html) {
  if (!html) return false;
  const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-hydration-key="cart-drawer-inner"]');
  const current = document.querySelector('cart-drawer-component [data-hydration-key="cart-drawer-inner"]');
  if (!fresh || !current || !drawerBodyPresent(fresh)) return false;

  const dialog = current.closest('dialog');
  const wasOpen = Boolean(dialog?.open);
  current.replaceWith(fresh);
  if (wasOpen && dialog && !dialog.open) dialog.showModal();
  return true;
}

function publishCart(data) {
  const html = sectionMarkup(data, data.sectionId);
  const itemCount = data.item_count ?? data.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;
  try {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          data: {
            sections: data.sections,
            itemCount,
            source: 'cw-cart-drawer',
          },
        },
      })
    );
  } catch (error) {
    console.error(error);
  }
  replaceDrawerInner(html);
  requestAnimationFrame(() => {
    replaceDrawerInner(html);
    syncUpsellNav(document);
  });
}

async function drawerSectionHtml(cart, sectionId) {
  const html = sectionMarkup(cart, sectionId);
  if (html.includes('cart-drawer__content') || html.includes('cart-drawer__heading--empty')) return html;

  const url = new URL(window.location.href);
  url.searchParams.set('sections', sectionId);
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) return html;
  const data = await response.json();
  return typeof data?.[sectionId] === 'string' ? data[sectionId] : html;
}

function cartEndpoint(url) {
  return url.endsWith('.js') ? url : `${url}.js`;
}

async function postCart(url, payload) {
  const response = await fetch(cartEndpoint(url), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  return readCart(response);
}

async function loadDrawerCart(sectionId) {
  const response = await fetch(`${cartEndpoint(Theme.routes.cart_url)}?sections=${encodeURIComponent(sectionId)}`);
  return readCart(response);
}

function syncUpsellNav(root) {
  const scope = root || document;
  const sections = scope.matches?.('.cw-cart-upsell') ? [scope] : scope.querySelectorAll('.cw-cart-upsell');
  sections.forEach((section) => {
    const track = section.querySelector('.cw-cart-upsell__track');
    const prev = section.querySelector('.cw-cart-upsell__nav--prev');
    const next = section.querySelector('.cw-cart-upsell__nav--next');
    if (!track || !prev || !next) return;
    const cards = track.querySelectorAll('.cw-cart-upsell__card');
    const first = cards[0];
    const last = cards[cards.length - 1];
    const start = first ? first.offsetLeft : 0;
    const end = last ? last.offsetLeft + last.offsetWidth : track.scrollWidth;
    const canScroll = end - start > track.clientWidth + 4;
    prev.hidden = !canScroll || track.scrollLeft <= start + 4;
    next.hidden = !canScroll || track.scrollLeft + track.clientWidth >= end - 4;
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cw-upsell-dir]');
  if (!(button instanceof HTMLButtonElement)) return;
  const track = button.closest('.cw-cart-upsell')?.querySelector('.cw-cart-upsell__track');
  if (!track) return;
  const cards = [...track.querySelectorAll('.cw-cart-upsell__card')];
  if (!cards.length) return;
  const dir = Number(button.dataset.cwUpsellDir) || 1;
  const current = cards.reduce((nearest, card) =>
    Math.abs(card.offsetLeft - track.scrollLeft) < Math.abs(nearest.offsetLeft - track.scrollLeft) ? card : nearest
  );
  const index = Math.min(cards.length - 1, Math.max(0, cards.indexOf(current) + dir));
  const section = button.closest('.cw-cart-upsell');
  const update = () => syncUpsellNav(section);
  track.addEventListener('scroll', update);
  track.addEventListener('scrollend', () => {
    track.removeEventListener('scroll', update);
    update();
  }, { once: true });
  track.scrollTo({ left: cards[index].offsetLeft, behavior: 'smooth' });
});

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!(track instanceof Element) || !track.classList.contains('cw-cart-upsell__track')) return;
    syncUpsellNav(track.closest('.cw-cart-upsell'));
  },
  true
);

document.addEventListener(
  'toggle',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('cart-drawer__dialog') || !dialog.open) return;
    const run = (animEvent) => {
      if (animEvent && animEvent.target !== dialog) return;
      dialog.removeEventListener('animationend', run);
      syncUpsellNav(dialog);
    };
    dialog.addEventListener('animationend', run);
    setTimeout(run, 450);
  },
  true
);

syncUpsellNav(document);

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cw-cart-add]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  const variantId = button.dataset.cwCartAdd;
  const sectionId = drawerSectionId(button);
  if (!variantId || !sectionId) return;

  button.disabled = true;
  try {
    const added = await postCart(Theme.routes.cart_add_url, {
      id: variantId,
      quantity: 1,
      sections: sectionId,
      sections_url: window.location.pathname,
    });
    let html = sectionMarkup(added, sectionId);
    let itemCount = itemCountFrom(html, added);
    if (!html || itemCount == null) {
      const cart = await loadDrawerCart(sectionId);
      html = sectionMarkup(cart, sectionId) || html;
      itemCount = cart.item_count ?? itemCountFrom(html, cart);
    }
    publishCart({
      item_count: itemCount ?? 0,
      sections: html ? { [sectionId]: html } : added.sections,
      sectionId,
    });
  } catch (error) {
    console.error(error);
    if (button.isConnected) button.disabled = false;
  }
});

document.addEventListener('click', (event) => {
  const summary = event.target.closest('.cart-drawer__summary');
  if (!summary) return;

  const open = event.target.closest('[data-cw-price-toggle]');
  const close = event.target.closest('[data-cw-price-close]');
  if (!open && !close) return;

  const panel = summary.querySelector('[data-cw-price-panel]');
  const compact = summary.querySelector('[data-cw-price-compact]');
  if (!panel || !compact) return;

  const show = Boolean(open);
  panel.hidden = !show;
  compact.hidden = show;
  if (open) open.setAttribute('aria-expanded', String(show));
}, true);

document.addEventListener('click', (event) => {
  const size = event.target.closest('[data-cw-size]');
  if (!size) {
    document.querySelectorAll('[data-cw-size-menu]').forEach((menu) => {
      menu.hidden = true;
    });
    return;
  }

  const toggle = event.target.closest('[data-cw-size-toggle]');
  if (toggle) {
    const menu = size.querySelector('[data-cw-size-menu]');
    const willOpen = menu.hidden;
    document.querySelectorAll('[data-cw-size-menu]').forEach((item) => {
      item.hidden = true;
    });
    menu.hidden = !willOpen;
    toggle.setAttribute('aria-expanded', String(willOpen));
    return;
  }

  const option = event.target.closest('[data-cw-size-option]');
  if (!option || option.disabled) return;

  const select = size.querySelector('[data-cw-line-size]');
  if (!select) return;
  select.value = option.dataset.cwSizeOption;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  size.querySelector('[data-cw-size-menu]').hidden = true;
  size.querySelector('[data-cw-size-toggle]')?.setAttribute('aria-expanded', 'false');
});

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-cw-remove-discount]');
  if (!(button instanceof HTMLButtonElement) || !button.closest('cart-drawer-component') || button.disabled) return;

  event.preventDefault();
  const sectionId = drawerSectionId(button);
  const removeCode = button.dataset.cwRemoveDiscount || '';
  const codes = [...document.querySelectorAll('.cw-cart-offer__row[data-discount-type="discount_code"]')]
    .map((row) => row.getAttribute('data-discount-code') || '')
    .filter((code) => code && code !== removeCode);
  if (!sectionId) return;

  button.disabled = true;
  try {
    const changed = await postCart(Theme.routes.cart_update_url, {
      discount: codes.join(','),
      sections: sectionId,
      sections_url: window.location.pathname + window.location.search,
    });
    const html = await drawerSectionHtml(changed, sectionId);
    publishCart({
      ...changed,
      sections: html ? { [sectionId]: html } : changed.sections,
      sectionId,
    });
  } catch (error) {
    console.error(error);
    if (button.isConnected) button.disabled = false;
  }
});

document.addEventListener('change', async (event) => {
  const select = event.target.closest('[data-cw-line-size]');
  if (!(select instanceof HTMLSelectElement) || select.disabled) return;

  const variantId = select.value;
  const lineKey = select.dataset.lineKey;
  const quantity = Number(select.dataset.qty) || 1;
  const sectionId = drawerSectionId(select);
  const previous = select.dataset.current || select.querySelector('option[selected]')?.value;
  if (!variantId || !lineKey || !sectionId || variantId === previous) return;

  select.disabled = true;
  let added = false;
  let changed = null;
  try {
    await postCart(Theme.routes.cart_add_url, {
      id: variantId,
      quantity,
    });
    added = true;
    changed = await postCart(Theme.routes.cart_change_url, {
      id: lineKey,
      quantity: 0,
      sections: sectionId,
      sections_url: window.location.pathname + window.location.search,
    });
  } catch (error) {
    console.error(error);
    if (added) {
      await postCart(Theme.routes.cart_change_url, {
        id: variantId,
        quantity: 0,
      }).catch(() => {});
    }
    if (previous && select.isConnected) select.value = previous;
    if (select.isConnected) select.disabled = false;
    return;
  }

  try {
    const html = await drawerSectionHtml(changed, sectionId);
    publishCart({
      ...changed,
      sections: html ? { [sectionId]: html } : changed.sections,
      sectionId,
    });
  } catch (error) {
    console.error(error);
  }
});

const CONFETTI_COLORS = ['DodgerBlue', 'OliveDrab', 'Gold', 'pink', 'SlateBlue', 'lightblue', 'Violet', 'PaleGreen', 'SteelBlue', 'SandyBrown', 'Chocolate', 'Crimson'];
const CONFETTI_COUNT = 110;
const CONFETTI_RAIN_MS = 2400;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

let confettiFrame = 0;
let confettiCanvas = null;
let confettiSeen = null;
let confettiQueued = false;

function unlockedTierCount(root) {
  return root?.querySelectorAll('.cw-cart-progress__tier.is-unlocked').length || 0;
}

function playCartConfetti(dialog) {
  if (!dialog?.open || REDUCED_MOTION.matches) return;

  if (!confettiCanvas?.isConnected) {
    confettiCanvas = document.createElement('canvas');
    confettiCanvas.className = 'cw-cart-confetti';
    confettiCanvas.setAttribute('aria-hidden', 'true');
    dialog.appendChild(confettiCanvas);
  }

  const width = Math.max(1, dialog.clientWidth);
  const height = Math.max(1, dialog.clientHeight);
  const dpr = Math.min(window.devicePixelRatio || 1, 1.25);
  const bufferWidth = Math.round(width * dpr);
  const bufferHeight = Math.round(height * dpr);
  if (confettiCanvas.width !== bufferWidth || confettiCanvas.height !== bufferHeight) {
    confettiCanvas.width = bufferWidth;
    confettiCanvas.height = bufferHeight;
  }

  const ctx = confettiCanvas.getContext('2d', { alpha: true });
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.lineCap = 'butt';

  const groups = [];
  const groupOf = new Map();
  for (let index = 0; index < CONFETTI_COUNT; index += 1) {
    const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
    const size = 8 + Math.random() * 14;
    const line = Math.max(4, Math.round(size / 2));
    const key = color + line;
    let group = groupOf.get(key);
    if (!group) {
      group = { color, line, pieces: [] };
      groupOf.set(key, group);
      groups.push(group);
    }
    group.pieces.push({
      x: Math.random() * width,
      y: index < 50 ? Math.random() * height * 0.8 : Math.random() * height - height,
      quarter: size * 0.25,
      fall: (3 + size * 0.5) * 0.5,
      phase: Math.random() * CONFETTI_COUNT + 10,
      tiltPhase: index / 3,
      tiltAngle: 0,
      tiltStep: Math.random() * 0.07 + 0.05,
      tilt: 0,
    });
  }

  let angle = 0;
  let raining = true;
  let last = performance.now();
  const rainUntil = last + CONFETTI_RAIN_MS;
  const groupCount = groups.length;
  if (confettiFrame) cancelAnimationFrame(confettiFrame);

  const step = (now) => {
    if (!dialog.open) {
      ctx.clearRect(0, 0, width, height);
      confettiFrame = 0;
      return;
    }

    const dt = Math.min((now - last) * 0.06, 1.75);
    last = now;
    if (now >= rainUntil) raining = false;
    angle += 0.01 * dt;
    const drift = Math.sin(angle) * dt;
    ctx.clearRect(0, 0, width, height);

    let visible = 0;
    for (let groupIndex = 0; groupIndex < groupCount; groupIndex += 1) {
      const group = groups[groupIndex];
      const list = group.pieces;
      const count = list.length;
      let drawn = 0;
      ctx.beginPath();
      ctx.lineWidth = group.line;
      ctx.strokeStyle = group.color;

      for (let index = 0; index < count; index += 1) {
        const piece = list[index];
        if (!raining && piece.y < -15) {
          piece.y = height + 40;
          continue;
        }

        piece.tiltAngle += piece.tiltStep * dt;
        piece.y += (Math.cos(angle + piece.phase) * 0.5 + piece.fall) * dt;
        piece.x += drift;
        piece.tilt = Math.sin(piece.tiltAngle - piece.tiltPhase) * 15;

        if (piece.y <= height) visible += 1;
        if (raining && (piece.x > width + 20 || piece.x < -20 || piece.y > height)) {
          piece.x = Math.random() * width;
          piece.y = -12;
        }
        if (piece.y < -24 || piece.y > height + 24) continue;

        drawn += 1;
        const top = piece.y;
        const lean = piece.tilt;
        ctx.moveTo(piece.x + lean + piece.quarter, top);
        ctx.lineTo(piece.x + lean, top + lean + piece.quarter);
      }

      if (drawn) ctx.stroke();
    }

    confettiFrame = visible ? requestAnimationFrame(step) : 0;
  };

  confettiFrame = requestAnimationFrame(step);
}

function celebrateUnlockedTiers() {
  const count = unlockedTierCount(confettiDialog);
  if (confettiSeen == null) {
    confettiSeen = count;
    return;
  }

  if (count > confettiSeen) {
    if (confettiDialog?.open) playCartConfetti(confettiDialog);
    else confettiQueued = true;
  } else if (count < confettiSeen) {
    confettiQueued = false;
  }
  confettiSeen = count;
}

const confettiRoot = document.querySelector('cart-drawer-component');
const confettiDialog = confettiRoot?.querySelector('.cart-drawer__dialog');
if (confettiRoot && confettiDialog) {
  confettiSeen = unlockedTierCount(confettiDialog);
  let confettiWatch = 0;
  let progressNode = null;
  let progressObserver = null;

  const scheduleConfettiCheck = () => {
    if (confettiWatch) return;
    confettiWatch = requestAnimationFrame(() => {
      confettiWatch = 0;
      const progress = confettiDialog.querySelector('.cw-cart-progress');
      if (progress !== progressNode) {
        progressObserver?.disconnect();
        progressNode = progress;
        progressObserver = null;
        if (progress) {
          progressObserver = new MutationObserver(scheduleConfettiCheck);
          progressObserver.observe(progress, {
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }
      celebrateUnlockedTiers();
    });
  };

  scheduleConfettiCheck();
  new MutationObserver(scheduleConfettiCheck).observe(confettiDialog, { childList: true, subtree: true });

  confettiRoot.addEventListener('toggle', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open || !confettiQueued) return;
    confettiQueued = false;
    playCartConfetti(dialog);
  }, true);
}
